import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { SrInsightType as InsightType } from '@plato/data/starrocks/types';
import { NumberOrEmpty } from '@plato/ui-legacy/components/typography/number-or-empty';
import { PercentageOrEmpty } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { valueFormatter } from '@plato/ui-legacy/lib/chart-helpers';
import { formatMonthShort, formatMonthYearShort } from '@plato/ui-legacy/lib/date-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { roundAbsToHundred } from '@plato/ui-legacy/lib/round-abs-to-hundred';
import { InfoHint, InfoHintContent, InfoHintTrigger } from '@plato/ui/components/info-hint';
import { TrendChart, TrendChartLegend } from '@plato/ui/components/trend-chart';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
} from '@plato/ui/containers/dialog';
import { Button } from '@plato/ui/primitives/button';
import { Skeleton } from '@plato/ui/primitives/skeleton';
import { Typography } from '@plato/ui/primitives/typography';
import { cn } from '@plato/ui/util/classes';
import {
    ArrowUpRightIcon,
    FolderIcon,
    PackageIcon,
    PlusIcon,
    ThumbsDownIcon,
    ThumbsUpIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type {
    InsightListItem,
    InsightProductRecommendationItem,
} from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { ChartLoading } from '../../charts/components/chart-loading.tsx';
import { useDialogs } from '../../dialogs/all-dialogs.tsx';
import type { SendAnalyticsEventHandler } from '../hooks/use-insights-with-analytics.ts';
import { getInsightBreadcrumbTrail, getInsightCategoryCrumb } from '../lib/insight-entity-link.ts';
import { getInsightEntityName } from '../lib/insight-entity-name.ts';
import { getInsightName } from '../lib/insight-name.ts';
import { InsightReasonText } from './insight-reason-text.tsx';
import { InsightTypeInfoHint } from './insight-type-info-hint.tsx';
import { MoneyDeltaBadge } from './money-delta-badge.tsx';
import {
    groupProductRecommendationsByBrandPreference,
    supportsBrandPreferenceGrouping,
} from './product-recommendations-brand-groups.ts';

type ArticleColumns = { priceRange: boolean; avgQuantity: boolean };

// Fixed widths: every row and the header is its own grid, so `auto` columns would not line up.
function articleRowGridCols({ priceRange, avgQuantity }: ArticleColumns): string {
    if (priceRange && avgQuantity) {
        return 'grid-cols-[1fr_9rem_3.5rem_1.5rem]';
    }
    if (priceRange) {
        return 'grid-cols-[1fr_9rem_1.5rem]';
    }
    return avgQuantity ? 'grid-cols-[1fr_3.5rem_1.5rem]' : 'grid-cols-[1fr_1.5rem]';
}

// The 25th/75th percentile can land on the exact same order price (e.g. a product with
// only one distinct sale price in the window) — treat anything within half a cent as equal
// rather than a real range.
function isPriceRangeDegenerate(low: number, high: number): boolean {
    return Math.abs(low - high) < 0.005;
}

// Whole euros read cleanly, but not for cheap articles: €0.04 at 0 decimals is "€0".
function pickPriceDecimals(price: number): number {
    return price < 10 ? 2 : 0;
}

function PriceRangeValue({ low, high }: { low: number; high: number }): ReactNode {
    const { t } = useTranslation();

    if (isPriceRangeDegenerate(low, high) || (low >= 10 && Math.round(low) === Math.round(high))) {
        return <CurrencyOrEmpty decimals={pickPriceDecimals(low)}>{low}</CurrencyOrEmpty>;
    }

    return (
        <>
            <CurrencyOrEmpty decimals={pickPriceDecimals(low)}>{low}</CurrencyOrEmpty>{' '}
            {t('article_price_range_separator', { ns: 'insights' })}{' '}
            <CurrencyOrEmpty decimals={pickPriceDecimals(high)}>{high}</CurrencyOrEmpty>
        </>
    );
}

export type InsightDialogProps = {
    open: boolean;
    onClose: () => void;
    insightData: InsightListItem;
    onCreateTask?: () => void;
    onAnalyticsEvent: SendAnalyticsEventHandler;
    onResolve: () => void;
    onNotRelevant: () => void;
};

export function InsightDialog({
    open,
    onClose,
    insightData,
    onCreateTask,
    onAnalyticsEvent,
    onResolve,
    onNotRelevant,
}: InsightDialogProps): ReactNode {
    const { t } = useTranslation();

    const name = getInsightName(t, insightData.insight);
    const category = getInsightCategoryCrumb(t, insightData);

    // Rendered twice — under the title on mobile, beside it on desktop — and
    // CSS shows the one for the current breakpoint.
    const amount = (
        <>
            <MoneyDeltaBadge>{roundAbsToHundred(insightData.insight.absolute_number ?? 0)}</MoneyDeltaBadge>
            <InsightTypeInfoHint insightData={insightData} />
        </>
    );

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogContent>
                <DialogHeader className="items-start">
                    <div className="flex min-w-0 flex-1 flex-col gap-tight">
                        {/* Insight titles are long sentences — wrap instead of truncating. */}
                        <DialogTitle className="whitespace-normal">{name}</DialogTitle>
                        {category.href ? (
                            <Link
                                to={category.href}
                                className="group flex min-w-0 items-center gap-tight"
                                onClick={() => {
                                    onAnalyticsEvent({
                                        event: 'sales_insight_actionable_entity_clicked',
                                        payload: { sales_insight_id: insightData.insight.id },
                                    });
                                }}
                            >
                                <Typography
                                    variant="body"
                                    className="truncate text-content-weak transition-colors group-hover:text-content group-hover:underline"
                                >
                                    {category.title}
                                </Typography>
                                <ArrowUpRightIcon className="size-4 shrink-0 text-content-weak transition-colors group-hover:text-content" />
                            </Link>
                        ) : (
                            <Typography variant="body" className="truncate text-content-weak">
                                {category.title}
                            </Typography>
                        )}
                        {/* On mobile the amount sits under the title so the title keeps the full width. */}
                        <div className="flex items-center gap-cozy pt-tight sm:hidden">{amount}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-cozy">
                        <div className="hidden items-center gap-cozy sm:flex">{amount}</div>
                        <DialogIconClose label={t('form__close', { ns: 'common' })} />
                    </div>
                </DialogHeader>
                <DialogBody>
                    <div className="flex flex-col gap-relaxed">
                        <InsightDetail insightData={insightData} onAnalyticsEvent={onAnalyticsEvent} />
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="secondary"
                        className="mr-auto"
                        onClick={() => {
                            onClose();
                            onNotRelevant();
                        }}
                    >
                        <ThumbsDownIcon /> {t('action__mark_as_not_relevant', { ns: 'insights' })}
                    </Button>
                    {onCreateTask && (
                        <Button
                            onClick={() => {
                                onClose();
                                onCreateTask();
                            }}
                        >
                            <PlusIcon /> {t('action__create_task', { ns: 'insights' })}
                        </Button>
                    )}
                    <Button
                        variant="primary"
                        onClick={() => {
                            onClose();
                            onResolve();
                        }}
                    >
                        <ThumbsUpIcon /> {t('action__resolve', { ns: 'insights' })}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

type InsightDetailProps = {
    insightData: InsightListItem;
    onAnalyticsEvent: SendAnalyticsEventHandler;
};

function InsightDetail(props: InsightDetailProps): ReactNode {
    const insightType = props.insightData.insight.insight_type;

    switch (insightType) {
        case InsightType.CHURN_WARNING:
        case InsightType.GLOBAL_CHURN_WARNING:
            return <InsightChurnWarningDetail {...props} />;

        case InsightType.CROSS_SELL:
        case InsightType.FIRST_PURCHASE:
        case InsightType.SHARE_OF_WALLET:
        case InsightType.PREVIOUSLY_PURCHASED:
        case InsightType.INDUSTRY_TOP:
        case InsightType.GLOBAL_TOP:
            return <InsightProductDetail {...props} />;

        default:
            return null;
    }
}

// The reasoning behind the insight plus its taxonomy breadcrumb — shared by
// every insight type, sits between the chart (if any) and the article list.
function InsightExplainer({ insightData }: { insightData: InsightListItem }): ReactNode {
    const { breadcrumbDepthsToHide } = useAppConfig();
    const { t } = useTranslation();
    const { insight } = insightData;
    const entityName = getInsightEntityName(t, insightData);
    const trail = getInsightBreadcrumbTrail(t, insightData, breadcrumbDepthsToHide);

    return (
        <div className="flex flex-col gap-cozy">
            <Typography variant="body" className="text-content">
                <InsightReasonText insight={insight} categoryName={entityName} />
            </Typography>
            {trail.length > 0 && (
                <div className="flex items-start gap-tight text-content-weak">
                    <FolderIcon className="mt-px size-3.5 shrink-0" />
                    <Typography variant="caption" className="min-w-0 break-words">
                        {trail.map((crumb) => (
                            <span
                                key={crumb.id}
                                className="after:mx-tight after:content-['/'] last:after:content-none"
                            >
                                {crumb.href ? (
                                    <Link
                                        to={crumb.href}
                                        className="inline-flex items-center gap-0.5 align-middle transition-colors hover:text-content hover:underline"
                                    >
                                        {crumb.title}
                                        <ArrowUpRightIcon className="size-3.5 shrink-0" />
                                    </Link>
                                ) : (
                                    crumb.title
                                )}
                            </span>
                        ))}
                    </Typography>
                </div>
            )}
        </div>
    );
}

function InsightChurnWarningDetail({ insightData }: InsightDetailProps): ReactNode {
    const { t } = useTranslation();
    const locale = useResolvedLocale();
    const { defaultCurrency: currency } = useAppConfig();
    const { openProductDetails } = useDialogs();

    const extendedView = api.platoUser.query.insights.getInsightExtendedView;

    const detailProcedure =
        insightData.insight.insight_type === InsightType.GLOBAL_CHURN_WARNING
            ? extendedView.getGlobalChurnWarningDetail
            : extendedView.getChurnWarningDetail;
    const aggregateProcedure =
        insightData.insight.insight_type === InsightType.GLOBAL_CHURN_WARNING
            ? extendedView.getGlobalChurnWarningAggregate
            : extendedView.getChurnWarningCategoryAggregate;

    const { data: productData, isLoading: isLoadingProducts } = detailProcedure.useQuery({
        insightId: insightData.insight.id,
    });

    const { data: categoryData, isLoading: isLoadingCategory } = aggregateProcedure.useQuery({
        insightId: insightData.insight.id,
    });

    // Backend returns 24 months sorted oldest → newest. Frame it year-over-year:
    // the last 12 months are the `current` line, each point overlaid with the
    // same month a year earlier (`previous`) so the decline reads at a glance.
    const months = categoryData?.monthly_sales_over_last_2_years ?? [];
    const currentMonths = months.slice(-12);
    const currentStart = months.length - currentMonths.length;

    const chartData = currentMonths.map((month, index) => {
        const [year, monthStr] = month.monthYear.split('-');
        const label =
            year && monthStr
                ? formatMonthYearShort(
                      new Date(Number.parseInt(year, 10), Number.parseInt(monthStr, 10) - 1),
                      locale,
                  )
                : month.monthYear;
        // Same calendar month a year earlier, when that far back exists.
        const previousMonth = months[currentStart + index - 12];
        return { label, current: month.monthly_sales, previous: previousMonth?.monthly_sales };
    });

    const monthShort = (monthYear: string): string => {
        const [year, monthStr] = monthYear.split('-');
        return year && monthStr
            ? formatMonthShort(new Date(Number.parseInt(year, 10), Number.parseInt(monthStr, 10) - 1), locale)
            : monthYear;
    };

    const firstMonthYear = currentMonths.at(0)?.monthYear;
    const lastMonthYear = currentMonths.at(-1)?.monthYear;
    const startLabel = firstMonthYear ? monthShort(firstMonthYear) : undefined;
    const endLabel = lastMonthYear ? monthShort(lastMonthYear) : undefined;
    const currentYear = lastMonthYear?.split('-')[0];
    const currentLabel = currentYear ?? '';
    const previousLabel = currentYear ? String(Number.parseInt(currentYear, 10) - 1) : '';

    return (
        <>
            <div className="flex flex-col gap-default">
                {isLoadingCategory ? (
                    <ChartLoading className="h-48" />
                ) : (
                    <div className="flex flex-col gap-tight">
                        <TrendChart
                            data={chartData}
                            currentLabel={currentLabel}
                            previousLabel={previousLabel}
                            formatValue={(val) => valueFormatter(val, locale, currency)}
                            className="h-48"
                        />
                        <TrendChartLegend
                            currentLabel={currentLabel}
                            previousLabel={previousLabel}
                            startLabel={startLabel}
                            endLabel={endLabel}
                        />
                    </div>
                )}
            </div>

            <InsightExplainer insightData={insightData} />

            <div className="flex flex-col border-t border-line-weak pt-default">
                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 pb-cozy">
                    <Typography variant="caption" className="text-content-weak">
                        {t('churn_warning_extended_table_header__product', { ns: 'insights' })}
                    </Typography>
                    <Typography variant="caption" className="text-right text-content-weak">
                        {t('churn_warning_extended_table_header__this_period', { ns: 'insights' })}
                    </Typography>
                    <Typography variant="caption" className="text-right text-content-weak">
                        {t('churn_warning_extended_table_header__delta', { ns: 'insights' })}
                    </Typography>
                    <span />
                </div>
                {isLoadingProducts ? (
                    <ArticleListLoading />
                ) : (
                    <div className="divide-y divide-line-weak">
                        {(productData ?? []).map((product) => {
                            const delta = product.revenue_delta_percentage;
                            const up = delta === null || delta === 0 ? null : delta > 0;
                            return (
                                <button
                                    key={product.id}
                                    type="button"
                                    onClick={() => {
                                        openProductDetails(product.id, insightData.insight.customer_id);
                                    }}
                                    className="group grid w-full grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 py-cozy text-left"
                                >
                                    <ArticleName
                                        code={product.id}
                                        name={product.name}
                                        brand={product.brand}
                                    />
                                    <Typography
                                        variant="body"
                                        className="text-right tabular-nums text-content"
                                    >
                                        <CurrencyOrEmpty showEmptyIfZero>
                                            {product.revenue_current}
                                        </CurrencyOrEmpty>
                                    </Typography>
                                    <Typography
                                        variant="body"
                                        className={cn(
                                            'text-right tabular-nums',
                                            up === null
                                                ? 'text-content-weak'
                                                : up
                                                  ? 'text-success'
                                                  : 'text-danger',
                                        )}
                                    >
                                        <PercentageOrEmpty showPlusSignIfPositive showEmptyIfZero>
                                            {delta}
                                        </PercentageOrEmpty>
                                    </Typography>
                                    <ArrowUpRightIcon className="size-4 shrink-0 self-center text-content-weak transition-colors group-hover:text-content" />
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
}

function InsightProductDetail({ insightData, onAnalyticsEvent }: InsightDetailProps): ReactNode {
    const { openProductDetails } = useDialogs();
    const { t } = useTranslation();
    const { data, isLoading } =
        api.platoUser.query.insights.getInsightExtendedView.getProductRecommendationsDetail.useQuery({
            insightId: insightData.insight.id,
        });

    const items = data ?? [];
    const groups = supportsBrandPreferenceGrouping(insightData.insight.insight_type)
        ? groupProductRecommendationsByBrandPreference(items)
        : ({ hasBrandPreference: false, items } as const);
    const columns: ArticleColumns = {
        priceRange: items.some((item) => item.price_range !== undefined),
        avgQuantity: items.some((item) => item.avg_order_quantity !== undefined),
    };
    const showHeader = columns.priceRange || columns.avgQuantity;
    const priceRangeLabel = t('product_recommendations_table_header__price_range', { ns: 'insights' });
    const avgQuantityLabel = t('product_recommendations_table_header__avg_order_quantity', {
        ns: 'insights',
    });
    const columnsTooltip = (
        <div className="flex flex-col gap-tight">
            {columns.priceRange && (
                <span>
                    <span className="font-semibold">{priceRangeLabel}:</span>{' '}
                    {t('product_recommendations_table_header__price_range_tooltip', { ns: 'insights' })}
                </span>
            )}
            {columns.avgQuantity && (
                <span>
                    <span className="font-semibold">{avgQuantityLabel}:</span>{' '}
                    {t('product_recommendations_table_header__avg_order_quantity_tooltip', {
                        ns: 'insights',
                    })}
                </span>
            )}
        </div>
    );

    function handleSelect(product: InsightProductRecommendationItem): void {
        onAnalyticsEvent({
            event: 'sales_insight_expanded_entity_clicked',
            payload: {
                sales_insight_id: insightData.insight.id,
                clicked_product_id: product.product_variant_id,
                clicked_product_name: product.product_variant_name,
                clicked_product_position: items.indexOf(product),
                visible_products: items.map((item) => ({
                    product_id: item.product_variant_id,
                    product_name: item.product_variant_name,
                })),
            },
        });
        openProductDetails(product.product_variant_id, insightData.insight.customer_id);
    }

    return (
        <>
            <InsightExplainer insightData={insightData} />
            <div className={cn('flex flex-col', showHeader && 'border-t border-line-weak pt-default')}>
                {showHeader && (
                    <div className={cn('grid items-center gap-x-2 pb-cozy', articleRowGridCols(columns))}>
                        <Typography variant="caption" className="text-content-weak">
                            {t('product_recommendations_table_header__product', { ns: 'insights' })}
                        </Typography>
                        {columns.priceRange && (
                            <ColumnHeader
                                label={priceRangeLabel}
                                tooltip={columns.avgQuantity ? undefined : columnsTooltip}
                            />
                        )}
                        {columns.avgQuantity && (
                            <ColumnHeader label={avgQuantityLabel} tooltip={columnsTooltip} />
                        )}
                        <div className="w-6" />
                    </div>
                )}
                {isLoading ? (
                    <ArticleListLoading />
                ) : groups.hasBrandPreference ? (
                    <>
                        <ProductRecommendationsGroup
                            title={t('product_recommendations__preferred_brands_header', { ns: 'insights' })}
                            tooltip={t('product_recommendations__preferred_brands_tooltip', {
                                ns: 'insights',
                            })}
                            products={groups.preferred}
                            columns={columns}
                            onSelect={handleSelect}
                        />
                        <ProductRecommendationsGroup
                            title={t('product_recommendations__other_items_header', { ns: 'insights' })}
                            products={groups.other}
                            columns={columns}
                            onSelect={handleSelect}
                        />
                    </>
                ) : (
                    <div className="divide-y divide-line-weak">
                        {groups.items.map((product) => (
                            <ProductRow
                                key={product.product_variant_id}
                                product={product}
                                brand={product.brand}
                                columns={columns}
                                onSelect={handleSelect}
                            />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}

function ProductRecommendationsGroup({
    title,
    tooltip,
    products,
    columns,
    onSelect,
}: {
    title: string;
    tooltip?: string;
    products: InsightProductRecommendationItem[];
    columns: ArticleColumns;
    onSelect: (product: InsightProductRecommendationItem) => void;
}): ReactNode {
    if (products.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-tight">
            <div className="flex items-center gap-tight pt-cozy">
                <Typography variant="caption" className="font-semibold text-content">
                    {title}
                </Typography>
                {tooltip && (
                    <InfoHint>
                        <InfoHintTrigger />
                        <InfoHintContent side="top">{tooltip}</InfoHintContent>
                    </InfoHint>
                )}
            </div>
            <div className="divide-y divide-line-weak">
                {products.map((product) => (
                    <ProductRow
                        key={product.product_variant_id}
                        product={product}
                        brand={product.brand}
                        columns={columns}
                        onSelect={onSelect}
                    />
                ))}
            </div>
        </div>
    );
}

function ProductRow({
    product,
    brand,
    columns,
    onSelect,
}: {
    product: InsightProductRecommendationItem;
    brand?: string;
    columns: ArticleColumns;
    onSelect: (product: InsightProductRecommendationItem) => void;
}): ReactNode {
    return (
        <button
            type="button"
            onClick={() => {
                onSelect(product);
            }}
            className={cn(
                'group grid w-full items-center gap-x-2 py-cozy text-left',
                articleRowGridCols(columns),
            )}
        >
            <ArticleName
                code={product.product_variant_id}
                name={product.product_variant_name}
                brand={brand}
            />
            {columns.priceRange && (
                <Typography variant="body" className="text-right tabular-nums text-content">
                    {product.price_range ? (
                        <PriceRangeValue low={product.price_range.low} high={product.price_range.high} />
                    ) : (
                        '-'
                    )}
                </Typography>
            )}
            {columns.avgQuantity && (
                <Typography variant="body" className="text-right tabular-nums text-content">
                    <NumberOrEmpty>{product.avg_order_quantity}</NumberOrEmpty>
                </Typography>
            )}
            <div className="flex w-6 justify-end">
                <ArrowUpRightIcon className="size-4 shrink-0 self-center text-content-weak transition-colors group-hover:text-content" />
            </div>
        </button>
    );
}

function ArticleName({ code, name, brand }: { code: string; name: string; brand?: string }): ReactNode {
    return (
        <div className="flex min-w-0 items-center gap-cozy">
            <PackageIcon className="size-5 shrink-0 text-content-weak transition-colors group-hover:text-content" />
            <div className="flex min-w-0 flex-col gap-0.5">
                <Typography variant="body" className="truncate text-content group-hover:underline">
                    {name}
                </Typography>
                <Typography variant="caption" className="truncate text-content-weak">
                    {brand ? `${code} · ${brand}` : code}
                </Typography>
            </div>
        </div>
    );
}

function ColumnHeader({ label, tooltip }: { label: string; tooltip?: ReactNode }): ReactNode {
    return (
        <div className="relative flex items-center justify-end">
            <Typography variant="caption" className="whitespace-nowrap text-right text-content-weak">
                {label}
            </Typography>
            {tooltip && (
                // Floats outside the column edge so the label stays flush with the values below.
                <div className="absolute left-full top-1/2 ml-1 -translate-y-1/2">
                    <InfoHint>
                        <InfoHintTrigger />
                        <InfoHintContent side="top">{tooltip}</InfoHintContent>
                    </InfoHint>
                </div>
            )}
        </div>
    );
}

/** Name widths per placeholder row, so the rows don't read as one repeated block. */
const loadingNameWidths = ['w-3/5', 'w-4/5', 'w-1/2'];

// Mirrors an article row: the name and the value are the dark shade, the icon and the
// id line the light one, as in the loaded row. Each bar sits in a box of its line's
// height, so the list doesn't jump when the rows arrive.
function ArticleListLoading(): ReactNode {
    return (
        <div className="divide-y divide-line-weak">
            {loadingNameWidths.map((nameWidth) => (
                <div key={nameWidth} className="flex items-center gap-x-4 py-cozy">
                    <div className="flex min-w-0 grow items-center gap-cozy">
                        <Skeleton className="size-5 shrink-0 rounded-default bg-content-weakest/50" />
                        <div className="flex min-w-0 grow flex-col gap-0.5">
                            <div className="flex h-5 items-center">
                                <Skeleton className={nameWidth} />
                            </div>
                            <div className="flex h-4 items-center">
                                <Skeleton className="h-2.5 w-16 bg-content-weakest/50" />
                            </div>
                        </div>
                    </div>
                    <Skeleton className="w-14 shrink-0" />
                </div>
            ))}
        </div>
    );
}
